/**
 * DownloadTray — minimisable global download queue.
 *
 * Every "download original" button in the app routes through
 * `useDownloadTray().enqueue({...})` instead of calling `downloadPrivateMedia`
 * directly. The tray lives fixed bottom-right; each entry shows a small
 * preview + filename + progress bar, streams the bytes via `fetch +
 * ReadableStream` for a real progress %, and triggers a native "Save As"
 * once complete. Failed entries stay visible with a retry. Completed
 * entries auto-dismiss after 6 s.
 *
 * Multiple concurrent downloads supported. The tray collapses to a tiny
 * "3 running" pill when minimised; click to expand.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, X, Check, AlertCircle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

export interface DownloadItemInput {
  /** Absolute PB URL to the original file (no thumb, no format override). */
  url: string;
  /** Desired filename for the download. */
  filename: string;
  /** Optional preview image URL for the tray row (tiny WebP works best). */
  previewUrl?: string;
}

interface DownloadItem extends DownloadItemInput {
  id: string;
  status: "pending" | "downloading" | "done" | "error";
  bytesReceived: number;
  bytesTotal: number;
  error?: string;
}

/* ── Global store (tiny pub/sub, no extra deps) ────────────── */

type Listener = () => void;
const _items: DownloadItem[] = [];
const _listeners = new Set<Listener>();
function notify() { for (const l of _listeners) l(); }
function subscribe(l: Listener) { _listeners.add(l); return () => { _listeners.delete(l); }; }

let _minimised = false;
let _minListeners: Listener[] = [];
function setMinimised(v: boolean) { _minimised = v; for (const l of _minListeners) l(); }
function subscribeMin(l: Listener) { _minListeners.push(l); return () => { _minListeners = _minListeners.filter((x) => x !== l); }; }

function upsert(item: DownloadItem) {
  const i = _items.findIndex((x) => x.id === item.id);
  if (i === -1) _items.push(item); else _items[i] = item;
  notify();
}
function remove(id: string) {
  const i = _items.findIndex((x) => x.id === id);
  if (i !== -1) { _items.splice(i, 1); notify(); }
}

/** Fetch with progress using ReadableStream. The `pb_auth` cookie is
 *  sent automatically on same-origin requests, so Caddy's asset gate
 *  treats us as an authenticated session.
 *
 *  Special case: `data:` URLs (editor outputs, Gemini inline bytes)
 *  bypass fetch entirely — our CSP's `connect-src` doesn't allow
 *  `data:`, and fetching a data URL is pointless anyway since the
 *  payload is already inline. We decode directly into a Blob. */
async function runDownload(item: DownloadItem): Promise<void> {
  const urlWithAuth = item.url;

  upsert({ ...item, status: "downloading", bytesReceived: 0 });

  // ── Fast path: data URL → direct decode, no network ──
  if (urlWithAuth.startsWith("data:")) {
    try {
      const comma = urlWithAuth.indexOf(",");
      const header = urlWithAuth.slice(0, comma);
      const payload = urlWithAuth.slice(comma + 1);
      const mimeMatch = header.match(/^data:([^;,]+)/i);
      const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";
      const isB64 = /;base64/i.test(header);
      let bytes: Uint8Array;
      if (isB64) {
        const bin = atob(payload);
        bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      } else {
        const decoded = decodeURIComponent(payload);
        bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      triggerSaveAs(URL.createObjectURL(blob), item.filename);
      upsert({ ...item, status: "done", bytesReceived: blob.size, bytesTotal: blob.size });
      scheduleRemove(item.id);
    } catch (err) {
      upsert({ ...item, status: "error", error: err instanceof Error ? err.message : "decode failed" });
    }
    return;
  }

  let res: Response;
  try {
    res = await fetch(urlWithAuth, { credentials: "same-origin" });
  } catch (err) {
    upsert({ ...item, status: "error", error: err instanceof Error ? err.message : "fetch failed" });
    return;
  }
  if (!res.ok) {
    upsert({ ...item, status: "error", error: `HTTP ${res.status}` });
    return;
  }

  const total = Number(res.headers.get("content-length") || 0);
  const reader = res.body?.getReader();
  if (!reader) {
    // Browser doesn't support streaming — fall back to blob()
    const blob = await res.blob();
    triggerSaveAs(URL.createObjectURL(blob), item.filename);
    upsert({ ...item, status: "done", bytesReceived: blob.size, bytesTotal: blob.size });
    scheduleRemove(item.id);
    return;
  }

  const chunks: Uint8Array[] = [];
  let received = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength;
      upsert({ ...item, status: "downloading", bytesReceived: received, bytesTotal: total });
    }
  }
  const blob = new Blob(chunks as BlobPart[], { type: res.headers.get("content-type") || "application/octet-stream" });
  triggerSaveAs(URL.createObjectURL(blob), item.filename);
  upsert({ ...item, status: "done", bytesReceived: received, bytesTotal: received });
  scheduleRemove(item.id);
}

function triggerSaveAs(blobUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

function scheduleRemove(id: string) { window.setTimeout(() => remove(id), 6000); }

/* ── Hook for consumers ────────────────────────────────────── */

export function useDownloadTray() {
  const enqueue = useCallback((input: DownloadItemInput) => {
    const id = `dl-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const item: DownloadItem = {
      ...input,
      id,
      status: "pending",
      bytesReceived: 0,
      bytesTotal: 0,
    };
    _items.push(item);
    notify();
    // Fire immediately; the tray shows progress live.
    void runDownload(item);
  }, []);

  return { enqueue };
}

/* ── The tray component ────────────────────────────────────── */

export default function DownloadTray() {
  const [, forceRerender] = useState(0);
  const [minimised, setMin] = useState(_minimised);

  useEffect(() => subscribe(() => forceRerender((x) => x + 1)), []);
  useEffect(() => subscribeMin(() => setMin(_minimised)), []);

  const items = _items;
  const activeCount = useMemo(
    () => items.filter((i) => i.status === "downloading" || i.status === "pending").length,
    [items],
  );

  if (items.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        right: 24,
        bottom: 24,
        width: minimised ? undefined : 340,
        zIndex: 2147483640,
        pointerEvents: "auto",
      }}
    >
      {minimised ? (
        <button
          onClick={() => { setMinimised(false); setMin(false); }}
          style={{
            display: "inline-flex",
            alignItems: "center", gap: 8,
            height: 40, padding: "0 14px 0 10px",
            background: "var(--bg-surface-raised)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-pill)",
            color: "var(--text-primary)",
            fontSize: "var(--font-size-sm)",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 20px 50px -24px rgba(0,0,0,0.3)",
          }}
        >
          <span style={{
            width: 24, height: 24,
            background: "var(--brand-gradient)",
            color: "#fff",
            borderRadius: "50%",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Download size={12} strokeWidth={1.75} />
          </span>
          {activeCount > 0 ? `${activeCount} downloading…` : `${items.length} done`}
          <ChevronUp size={12} strokeWidth={1.75} style={{ opacity: 0.6 }} />
        </button>
      ) : (
        <div
          style={{
            background: "var(--bg-surface-raised)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-xl)",
            boxShadow: "0 28px 60px -24px rgba(0,0,0,0.4)",
            overflow: "hidden",
          }}
        >
          <header style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 12px 10px 14px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-surface)",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              fontFamily: "var(--font-mono)",
              fontSize: 10, fontWeight: 700,
              color: "var(--text-tertiary)",
              textTransform: "uppercase", letterSpacing: "var(--tracking-label)",
            }}>
              <Download size={11} strokeWidth={1.75} style={{ color: "var(--accent-300)" }} />
              Downloads · {items.length}
            </span>
            <button
              aria-label="Minimise"
              onClick={() => { setMinimised(true); setMin(true); }}
              style={{
                width: 24, height: 24,
                background: "transparent", border: "none",
                color: "var(--text-tertiary)", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ChevronDown size={13} strokeWidth={1.75} />
            </button>
          </header>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {items.map((it) => <TrayRow key={it.id} item={it} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const TrayRow: React.FC<{ item: DownloadItem }> = ({ item }) => {
  const progress = item.bytesTotal > 0
    ? Math.min(100, Math.floor((item.bytesReceived / item.bytesTotal) * 100))
    : item.status === "done" ? 100 : 0;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "44px minmax(0, 1fr) 24px",
        gap: 10,
        padding: "10px 12px",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
      }}
    >
      <div style={{
        width: 44, height: 44,
        borderRadius: "var(--radius-md)",
        background: "var(--bg-surface-alt)",
        overflow: "hidden",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {item.previewUrl ? (
          <img src={item.previewUrl} alt="" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Download size={14} strokeWidth={1.5} style={{ color: "var(--text-tertiary)" }} />
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: "var(--font-size-xs)",
          fontWeight: 600,
          color: "var(--text-primary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginBottom: 4,
        }}>
          {item.filename}
        </div>
        <ProgressLine item={item} percent={progress} />
      </div>

      <RowAction item={item} />
    </div>
  );
};

const ProgressLine: React.FC<{ item: DownloadItem; percent: number }> = ({ item, percent }) => {
  if (item.status === "error") {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 4,
        fontSize: 10,
        color: "var(--status-danger)",
      }}>
        <AlertCircle size={10} strokeWidth={1.75} />
        {item.error ?? "Failed"}
      </div>
    );
  }
  if (item.status === "done") {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 4,
        fontSize: 10,
        color: "#22C55E",
      }}>
        <Check size={10} strokeWidth={2.2} />
        Done · {formatBytes(item.bytesReceived)}
      </div>
    );
  }
  return (
    <>
      <div style={{
        width: "100%",
        height: 4,
        background: "var(--bg-surface-alt)",
        borderRadius: 2,
        overflow: "hidden",
      }}>
        <span style={{
          display: "block",
          height: "100%",
          width: `${percent}%`,
          background: "var(--brand-gradient)",
          transition: "width 180ms linear",
        }} />
      </div>
      <div style={{
        marginTop: 4,
        fontFamily: "var(--font-mono)",
        fontSize: 9, fontWeight: 700,
        color: "var(--text-tertiary)",
        textTransform: "uppercase", letterSpacing: "var(--tracking-label)",
      }}>
        {item.bytesTotal > 0
          ? `${formatBytes(item.bytesReceived)} / ${formatBytes(item.bytesTotal)} · ${percent}%`
          : "fetching…"}
      </div>
    </>
  );
};

const RowAction: React.FC<{ item: DownloadItem }> = ({ item }) => {
  const retryRef = useRef(false);

  if (item.status === "error") {
    return (
      <button
        onClick={() => {
          if (retryRef.current) return;
          retryRef.current = true;
          void runDownload(item);
        }}
        aria-label="Retry"
        style={iconButtonStyle}
      >
        <RefreshCw size={12} strokeWidth={1.75} />
      </button>
    );
  }
  return (
    <button
      onClick={() => remove(item.id)}
      aria-label="Remove"
      style={iconButtonStyle}
    >
      <X size={12} strokeWidth={1.75} />
    </button>
  );
};

const iconButtonStyle: React.CSSProperties = {
  width: 24, height: 24,
  background: "transparent",
  border: "none",
  color: "var(--text-tertiary)",
  cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  borderRadius: 4,
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
